import React, { useMemo } from "react";
import "./AssessmentCriteriaOriginal.css";
import {
  AssessmentCriteria,
  AssessmentCriteriaInputValues,
} from "../../constants/Constants";
import { useCenter } from "../../context/CenterContext";
import { useSubject } from "../../context/SubjectContext";

const RadioGroup = ({ name, value, onChange, yesId, noId, naId, mode }) => (
  <div className="field is-grouped is-grouped-multiline" role="radiogroup">
    <div className="control">
      <label className="radio">
        <input
          type="radio"
          name={name}
          id={yesId}
          value="yes"
          checked={value === "yes"}
          onChange={onChange}
          disabled={mode === "view"}
        />{" "}
        Yes
      </label>
    </div>

    <div className="control">
      <label className="radio">
        <input
          type="radio"
          name={name}
          id={noId}
          value="no"
          checked={value === "no"}
          onChange={onChange}
          disabled={mode === "view"}
        />{" "}
        No
      </label>
    </div>

    <div className="control">
      <label className="radio">
        <input
          type="radio"
          name={name}
          id={naId}
          value="not_attempted"
          checked={value === "not_attempted"}
          onChange={onChange}
          disabled={mode === "view"}
        />{" "}
        Not attempted
      </label>
    </div>
  </div>
);

const getDisplayForAchieved = (val) => {
  if (val === AssessmentCriteriaInputValues.YES || val === "yes") return "Yes";

  if (val === AssessmentCriteriaInputValues.NO || val === "no") return "No";
  if (
    val === AssessmentCriteriaInputValues.NOT_ATTEMPTED ||
    val === "not_attempted"
  )
    return "Not attempted";
  return "";
};

const AssessmentCriteriaUpdatedTable = ({
  criteriaDetails,

  values,

  notes,

  candidateId,

  onChangeValue,

  onChangeNote,

  onCriteriaAchievedChange,

  mode = "edit",
}) => {
  const safeValues = values || {};

  const safeNotes = notes?.[candidateId] || {};

  const { centerNumber } = useCenter();

  const { subjectData } = useSubject();

  const subjectId = subjectData?.selectedSubjectId;

  // Read (fallback) using the same composite key. We DO NOT write on radio click.
  const storageKey = `updatedSelections_${centerNumber}_${candidateId}_${subjectId}`;
  const localSelections = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey)) || {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const handleRadioChange = (criteriaName, value) => {
    // Update in-memory state only
    onChangeValue(candidateId, criteriaName, value);

    // Recompute yes count from current in-memory values
    const updatedValues = {
      ...safeValues,

      [criteriaName]: value,
    };
    const yesCount = Object.values(updatedValues).filter(
      (v) => v === "yes",
    ).length;
    onCriteriaAchievedChange(candidateId, yesCount);

    // IMPORTANT: Do not write to localStorage here. Save will persist via the parent.
  };

  return (
    <div className="tabcard">
      <div className="table-scroll-x">
        <table className="table is-hoverable is-fullwidth is-vcentered">
          <thead>
            <tr>
              <th>Assessment criteria</th>
              <th>Description</th>
              <th>Original marking</th>
              <th>
                Has the candidate achieved the assessment criteria? (Updated
                marking)
              </th>
              <th>Evidence location / notes (optional)</th>
            </tr>
          </thead>

          <tbody>
            {criteriaDetails.map((row, idx) => {
              const criteriaName = row.assessmentCriteriaName;

              // Prefer in-memory values from parent; fall back to last saved localStorage value
              const current =
                safeValues[criteriaName] !== undefined
                  ? safeValues[criteriaName]
                  : (localSelections[criteriaName] ?? "");

              const noteVal = safeNotes[criteriaName] || "";

              const yesId = `upd_${candidateId}_${idx}_yes`;

              const noId = `upd_${candidateId}_${idx}_no`;

              const naId = `upd_${candidateId}_${idx}_na`;

              const highlight = row.isAgreed === false && mode !== "view";

              return (
                <tr
                  key={`${candidateId}-${criteriaName}`}
                  className={highlight ? "row-not-agreed" : ""}
                >
                  <td>{criteriaName}</td>
                  <td>{row.assessmentCriteriaDescription}</td>

                  <td>
                    {getDisplayForAchieved(
                      row.originalCentreAssessmentName ===
                        AssessmentCriteria.ACHIEVED
                        ? AssessmentCriteriaInputValues.YES
                        : row.originalCentreAssessmentName ===
                            AssessmentCriteria.NOT_ACHIEVED
                          ? AssessmentCriteriaInputValues.NO
                          : row.originalCentreAssessmentName ===
                              AssessmentCriteria.NOT_ATTEMPTED
                            ? AssessmentCriteriaInputValues.NOT_ATTEMPTED
                            : null,
                    )}
                  </td>

                  <td>
                    {mode === "view" ? (
                      <span>{getDisplayForAchieved(current)}</span>
                    ) : (
                      <RadioGroup
                        name={`${candidateId}-${criteriaName}`}
                        value={current}
                        yesId={yesId}
                        noId={noId}
                        naId={naId}
                        onChange={(e) =>
                          handleRadioChange(criteriaName, e.target.value)
                        }
                        mode={mode}
                      />
                    )}
                  </td>

                  <td className="p-0">
                    <div
                      className="textarea-wrapper"
                      style={{ position: "relative" }}
                    >
                      <textarea
                        className="textarea"
                        rows={2}
                        maxLength={250}
                        value={noteVal}
                        disabled={mode === "view"}
                        onChange={(e) =>
                          onChangeNote(
                            candidateId,
                            criteriaName,
                            e.target.value,
                          )
                        }
                        aria-label={`${criteriaName} evidence location / notes`}
                      />

                      {mode !== "view" && (
                        <span
                          className="char-counter"
                          style={{
                            position: "absolute",

                            bottom: "4px",

                            right: "8px",

                            fontSize: "12px",

                            color: "#666",
                          }}
                        >
                          {noteVal.length}/250
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssessmentCriteriaUpdatedTable;
